'use client';

import { useTransactionSummary } from '@/features/user';
import RechargeDialog from '@/features/dashboard/components/transactions/RechargeDialog';
import { type ReactNode, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PlusActivateDialog } from './PlusActivateDialog';
import { PlusPaymentDialog } from './PlusPaymentDialog';
import {
  DEFAULT_PLUS_PRICE_ID,
  ZANGIA_PLUS_PRICE_GROUP_ID,
  plusCopy,
  type PlusSelectableFeatureId,
} from './plusCopy';
import { IPrice } from '@/shared';
import { useActivateZangiaPlus } from '../../api/jobs.queries';
import { usePrices } from '../../hooks/usePrices';

export type PlusPurchaseOpenOptions = {
  extras?: PlusSelectableFeatureId[];
};

type PlusPurchaseFlowProps = {
  isEnglish?: boolean;
  children: (open: (options?: PlusPurchaseOpenOptions) => void) => ReactNode;
};

export function PlusPurchaseFlow({
  isEnglish = false,
  children,
}: PlusPurchaseFlowProps) {
  const t = isEnglish ? plusCopy.en : plusCopy.mn;
  const [showActivate, setShowActivate] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [selected, setSelected] = useState<IPrice>();
  const [selectedExtras, setSelectedExtras] = useState<
    PlusSelectableFeatureId[]
  >([]);
  const { mutateAsync: activatePlus, isPending: isPaying } =
    useActivateZangiaPlus();
  const { data: pricesData } = usePrices(ZANGIA_PLUS_PRICE_GROUP_ID);
  const { data: summary, refetch: refetchSummary } = useTransactionSummary();

  useEffect(() => {
    const prices = pricesData?.data;
    if (!prices?.length) {
      return;
    }
    setSelected((current) => {
      if (current) {
        return current;
      }
      return (
        prices.find((price) => price.id === DEFAULT_PLUS_PRICE_ID) ?? prices[0]
      );
    });
  }, [pricesData?.data]);

  const netBalance = summary?.netBalance ?? 0;
  const netBonusBalance = summary?.netBonusBalance ?? 0;
  const totalBalance = netBalance + netBonusBalance;

  const open = (options?: PlusPurchaseOpenOptions) => {
    if (options?.extras) {
      setSelectedExtras(options.extras);
    }
    setShowActivate(true);
  };

  const continueToPayment = () => {
    setShowActivate(false);
    setShowPayment(true);
  };

  const toggleExtra = (id: PlusSelectableFeatureId) => {
    setSelectedExtras((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const handlePay = async () => {
    if (!selected || totalBalance < selected.price) {
      return;
    }

    try {
      await activatePlus({
        itemId: selected.id,
        additional: selectedExtras,
      });
      toast.success(t.paySuccess);
      setShowPayment(false);
    } catch {
      // Error toast is handled by useActivateZangiaPlus
    }
  };

  const openRecharge = () => {
    setShowPayment(false);
    setShowRecharge(true);
  };

  const paymentShortfall = Math.max((selected?.price ?? 0) - totalBalance, 0);

  const handleRechargeComplete = async () => {
    await refetchSummary();
    setShowRecharge(false);
    setShowPayment(true);
  };

  return (
    <>
      {children(open)}
      <PlusActivateDialog
        isOpen={showActivate}
        isEnglish={isEnglish}
        selectedId={selected?.id ?? 0}
        selectedExtras={selectedExtras}
        onSelect={setSelected}
        onToggleExtra={toggleExtra}
        onClose={() => setShowActivate(false)}
        onContinue={continueToPayment}
      />
      <PlusPaymentDialog
        isOpen={showPayment}
        isEnglish={isEnglish}
        price={selected?.price ?? 0}
        netBalance={netBalance}
        netBonusBalance={netBonusBalance}
        isPaying={isPaying}
        onClose={() => setShowPayment(false)}
        onPay={() => void handlePay()}
        onRecharge={openRecharge}
      />
      <RechargeDialog
        isOpen={showRecharge}
        onClose={() => {
          setShowRecharge(false);
          setShowPayment(true);
        }}
        onRecharge={handleRechargeComplete}
        autoMode
        autoAmount={paymentShortfall}
      />
    </>
  );
}
